import { Form, Input, Flex } from 'antd';
import React from 'react';

const { Item: FormItem } = Form;

// Helper functions for parameter processing
const flattenParameters = (parameters, prefix = '') => {
  const flattened = [];

  parameters.forEach((param) => {
    if (param.children && param.children.length > 0) {
      // This is a parent parameter, recurse into children
      const childParams = flattenParameters(param.children, param.name);
      flattened.push(...childParams);
    } else {
      // This is a leaf parameter
      flattened.push({
        ...param,
        fieldPath: prefix ? `${prefix}.${param.name}` : param.name,
        parentPath: prefix,
      });
    }
  });

  return flattened;
};

function ActionParameterForm({ action, onFormsChange }) {
  const [actionForm] = Form.useForm();
  const disabled = !onFormsChange;

  // Get flattened parameters for form rendering
  const flattenedParams = React.useMemo(() => {
    if (!action.definition) return [];
    return flattenParameters(action.definition);
  }, [action.definition]);

  const hasInput = flattenedParams.some((param) => !param.read_only);

  // Handle form changes
  const handleFormChange = async () => {
    if (disabled) return;
    try {
      await actionForm.validateFields();
      onFormsChange({
        actionId: action.id,
        form: actionForm,
        isValid: true,
        definition: action.definition,
      });
    } catch (error) {
      const hasActualErrors = error.errorFields && error.errorFields.length > 0;
      const isValid = !hasActualErrors;
      onFormsChange({
        actionId: action.id,
        form: actionForm,
        isValid,
        definition: action.definition,
      });
    }
  };

  // Initialize form and validate immediately
  React.useEffect(() => {
    if (flattenedParams.length > 0) {
      const initialValues = {};
      flattenedParams.forEach((param) => {
        if (param.initial !== undefined) {
          initialValues[param.name] = param.initial;
        }
      });
      actionForm.setFieldsValue(initialValues);

      // Trigger initial validation
      handleFormChange();
    } else if (action.definition && !disabled) {
      // Action has definition but no flattened params (empty definition)
      onFormsChange({
        actionId: action.id,
        form: actionForm,
        isValid: true,
        definition: action.definition,
      });
    }
  }, [flattenedParams, actionForm]);

  if (!action.definition || flattenedParams.length === 0 || !hasInput) {
    return null;
  }

  return (
    <Form
      form={actionForm}
      layout="vertical"
      onValuesChange={handleFormChange}
      size="small"
      disabled={disabled}
      className="p-3"
    >
      <Flex vertical gap={8}>
        {flattenedParams.map((parameter) => {
          const {
            name, type, description, required, example_value, read_only,
          } = parameter;

          const getInputComponent = () => {
            const commonProps = {
              placeholder: example_value ? `e.g., ${example_value}` : `Enter ${description || name}`,
              disabled: read_only || disabled,
              addonBefore: name,
              size: 'medium',
            };

            switch (type) {
            case 'number':
            case 'integer':
              return (
                <Input
                  type="number"
                  {...commonProps}
                />
              );
            case 'email':
              return (
                <Input
                  type="email"
                  {...commonProps}
                />
              );
            case 'textarea':
              return (
                <Input.TextArea
                  rows={2}
                  {...commonProps}
                />
              );
            case 'string':
            case 'text':
            default:
              return (
                <Input
                  {...commonProps}
                />
              );
            }
          };

          return (
            <FormItem
              key={`${action.id}-${name}`}
              name={name}
              label={(
                <span>
                  {description || name}
                  {read_only && <span className="text-gray-500 text-xs"> (read-only)</span>}
                </span>
              )}
              hidden={read_only}
              rules={[
                {
                  required: required && !read_only,
                  message: `${description || name} is required`,
                },
                ...(type === 'email' ? [{
                  type: 'email',
                  message: 'Please enter a valid email address',
                }] : []),
              ]}
              className="mb-2"
            >
              {getInputComponent()}
            </FormItem>
          );
        })}
      </Flex>
    </Form>
  );
}

export { flattenParameters };
export default ActionParameterForm;
